import { useUser, useUserStats } from '@/entities/user/api/use-user'
import { Skeleton } from '@/shared/ui'
import { cn } from '@/shared/lib/utils'

/**
 * Премиальный хедер профиля
 */
export function ProfileHeader() {
  const { data: userData, isLoading: isLoadingUser } = useUser()
  const { data: statsData } = useUserStats()
  
  const user = userData?.data
  const stats = statsData?.data
  const totalEntries = stats?.diaryEntriesCount ?? 0
  const uniqueTeas = stats?.favoritesCount ?? 0

  // Определяем ранг
  const getRank = (entries: number) => {
    if (entries >= 100) return { name: 'Мастер чая', level: 5 }
    if (entries >= 50) return { name: 'Знаток', level: 4 }
    if (entries >= 20) return { name: 'Любитель', level: 3 }
    if (entries >= 10) return { name: 'Начинающий', level: 2 }
    return { name: 'Новичок', level: 1 }
  }

  const rank = getRank(totalEntries)

  if (isLoadingUser) {
    return (
      <div className="animate-in fade-in duration-200">
        <div className="flex items-center gap-4">
          <Skeleton className="w-16 h-16 rounded-full" />
          <div className="flex-1 space-y-2">
            <Skeleton className="h-6 w-32" />
            <Skeleton className="h-4 w-24" />
          </div>
        </div>
      </div>
    )
  }

  if (!user) return null

  return (
    <div className="animate-in fade-in slide-in-from-bottom-2 duration-300">
      {/* Верхняя часть - аватар и имя */}
      <div className="flex items-center gap-4 mb-6">
        {/* Аватар */}
        <div className="relative flex-shrink-0">
          {user.photoUrl ? (
            <img
              src={user.photoUrl}
              alt={user.firstName}
              className="w-16 h-16 rounded-full object-cover ring-2 ring-white dark:ring-dark-card shadow-lg"
            />
          ) : (
            <div className="w-16 h-16 rounded-full bg-gradient-to-br from-primary/80 to-primary flex items-center justify-center ring-2 ring-white dark:ring-dark-card shadow-lg">
              <span className="text-2xl font-bold text-white">
                {user.firstName[0]?.toUpperCase()}
              </span>
            </div>
          )}
          {/* Индикатор уровня */}
          <div className="absolute -bottom-1 -right-1 w-6 h-6 bg-gold rounded-full flex items-center justify-center ring-2 ring-white dark:ring-dark-card text-[10px] font-bold text-white">
            {rank.level}
          </div>
        </div>

        {/* Имя и ранг */}
        <div className="flex-1 min-w-0">
          <h1 className="text-xl font-bold text-ink dark:text-white truncate">
            {user.firstName} {user.lastName || ''}
          </h1>
          {user.username && (
            <p className="text-sm text-gray-500 dark:text-gray-400">
              @{user.username}
            </p>
          )}
          <div className="mt-1.5">
            <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-primary/10 dark:bg-primary/20 text-primary dark:text-primary-light">
              {rank.name}
            </span>
          </div>
        </div>
      </div>

      {/* Статистика */}
      <div className="grid grid-cols-2 gap-3">
        <StatCard 
          value={totalEntries} 
          label="Записей" 
          color="primary"
        />
        <StatCard 
          value={uniqueTeas} 
          label="В избранном" 
          color="success"
        />
      </div>
    </div>
  )
}

function StatCard({ 
  value, 
  label, 
  color 
}: { 
  value: number
  label: string
  color: 'primary' | 'success'
}) {
  return (
    <div className={cn(
      'px-4 py-3 rounded-xl',
      color === 'primary' 
        ? 'bg-primary/5 dark:bg-primary/10' 
        : 'bg-success/5 dark:bg-success/10'
    )}>
      <p className={cn(
        'text-2xl font-bold',
        color === 'primary' 
          ? 'text-primary dark:text-primary-light' 
          : 'text-success'
      )}>
        {value}
      </p>
      <p className="text-xs text-gray-500 dark:text-gray-400">{label}</p>
    </div>
  )
}
